import React from 'react';
import { Spinner } from 'react-bootstrap';
import { PLATFORM_FIELD_DATA, BasePriceRuleFormValue, OPTION_TYPE } from 'src/interfaces/Template.interface';
import {
  blockNegativeNumberInput,
  sanitizeNonNegativeNumericInput,
} from '../../utils/priceModifierValidation';
import { SectionHeaderLabel } from './SectionHeaderInfoTooltip.previous';
import Loader from 'src/utils/Loader';

interface DynamicPriceModifierRuleProps<T extends BasePriceRuleFormValue> {
  priceRules: T[];
  isLoading: boolean;
  onChange: (index: number, field: keyof T, value: any) => void;

  advancedMode?: boolean;
  hasPriceDateRows?: boolean;
  nativeFields?: PLATFORM_FIELD_DATA[];
  customFields?: PLATFORM_FIELD_DATA[];
  mappingFieldsLoading?: boolean;
  onMappingOptionChange?: (index: number, option: OPTION_TYPE) => void;

  fallbackUI: React.ReactNode;
}

const renderPriceTypeLabel = (
  rule: BasePriceRuleFormValue,
  index: number,
  className?: string
) => {
  if (!rule.description) {
    return <span className={className ?? 'mb-0 fw-semibold d-inline-block'}>{rule.priceType}</span>;
  }

  return (
    <span className={`${className ?? 'mb-0 fw-semibold'} d-inline-flex align-items-center`}>
      <SectionHeaderLabel
        title={rule.priceType}
        tooltip={rule.description}
        tooltipId={`price-type-${rule.templateFieldId}-${index}`}
      />
    </span>
  );
};

export const DynamicPriceModifierRule = <T extends BasePriceRuleFormValue>({
  priceRules,
  isLoading,
  onChange,
  advancedMode = false,
  hasPriceDateRows = false,
  nativeFields = [],
  customFields = [],
  mappingFieldsLoading = false,
  onMappingOptionChange,
  fallbackUI,
}: DynamicPriceModifierRuleProps<T>) => {

  const priceRuleGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: hasPriceDateRows
      ? '130px 145px 145px minmax(300px, 1fr) 130px 130px'
      : '160px 190px 190px minmax(360px, 1fr)',
    columnGap: hasPriceDateRows ? '12px' : '20px',
    width: '100%',
    minWidth: hasPriceDateRows ? '0' : '820px',
  };
  
  const priceRuleCellStyle: React.CSSProperties = { minWidth: 0 };

  const handleNumericChange = (index: number, field: keyof T, raw: string) => {
    onChange(index, field, sanitizeNonNegativeNumericInput(raw));
  };

  const nonNegativeNumberInputProps = {
    min: 0,
    step: 'any',
    onKeyDown: blockNegativeNumberInput,
  } as const;

  if (isLoading) {
    return (
      <div className='py-10 text-center'>
        <Loader variant="section" />
        <p className='mt-3 mb-0'>Loading price fields...</p>
      </div>
    );
  }

  if (priceRules.length === 0) {
    return <>{fallbackUI}</>;
  }

  if (!advancedMode) {
    return (
      <>
        {priceRules.map((rule, idx) => (
          <div key={rule.templateFieldId} className="row align-items-center g-3 mb-2">
            <div className="col-sm-3 text-sm-end">
              {renderPriceTypeLabel(rule, idx, 'form-label mb-0 fw-bold')}
            </div>
            <div className="col-sm-3 col-12">
              <select
                className="form-select"
                value={rule.adjustmentType.toLowerCase()}
                onChange={(e) => onChange(idx, 'adjustmentType' as keyof T, e.target.value.toUpperCase())}
              >
                <option value="NO_CHANGE">No Change</option>
                <option value="INCREASE">Increase</option>
                <option value="DECREASE">Decrease</option>
              </select>
            </div>
            <div className="col-sm-3 col-12 d-flex align-items-center">
              <label className="form-label mb-0 me-2">by</label>
              <input
                type="number"
                className="form-control"
                placeholder="Enter value"
                value={rule.adjustmentValue}
                disabled={rule.adjustmentType === 'NO_CHANGE'}
                {...nonNegativeNumberInputProps}
                onChange={(e) => handleNumericChange(idx, 'adjustmentValue' as keyof T, e.target.value)}
              />
            </div>
            <div className="col-sm-3 col-12">
              <select
                className="form-select"
                value={rule.adjustmentMode}
                disabled={rule.adjustmentType === 'NO_CHANGE'}
                onChange={(e) => onChange(idx, 'adjustmentMode' as keyof T, e.target.value)}
              >
                <option value="FLAT">Flat Amount</option>
                <option value="PERCENTAGE">Percent</option>
              </select>
            </div>
          </div>
        ))}
      </>
    );
  }

  return (
    <div className='overflow-auto'>
      <div className='fw-semibold text-gray-600 mb-3' style={priceRuleGridStyle}>
        <div style={priceRuleCellStyle}>Price Type</div>
        <div style={priceRuleCellStyle}>Mapping Type</div>
        <div style={priceRuleCellStyle}>Value</div>
        <div style={priceRuleCellStyle}>Price Adjustment</div>
        {hasPriceDateRows && (
          <>
            <div style={priceRuleCellStyle}>Start Date</div>
            <div style={priceRuleCellStyle}>End Date</div>
          </>
        )}
      </div>

      {priceRules.map((rule, index) => (
        <div
          key={`${rule.templateFieldId}-${index}`}
          className='align-items-center py-3 border-top'
          style={priceRuleGridStyle}
        >
          <div style={priceRuleCellStyle}>
            {renderPriceTypeLabel(rule, index)}
          </div>
          <div style={priceRuleCellStyle}>
            <select
              className='form-select'
              value={rule.mappingOption}
              onChange={(e) =>
                onMappingOptionChange && onMappingOptionChange(index, e.target.value as OPTION_TYPE)
              }
            >
              <option value='DEFAULT'>Default</option>
              <option value='NATIVE'>Native Field</option>
              <option value='CUSTOM'>Custom Field</option>
            </select>
          </div>
          <div style={priceRuleCellStyle}>
            {rule.mappingOption === 'DEFAULT' ? (
              <input
                className='form-control'
                type='number'
                name={`priceRules[${index}].defaultValue`}
                placeholder='Enter value'
                value={rule.defaultValue}
                {...nonNegativeNumberInputProps}
                onChange={(e) => handleNumericChange(index, 'defaultValue' as keyof T, e.target.value)}
              />
            ) : (
              <>
                <select
                  className='form-select'
                  value={rule.mappedField}
                  disabled={mappingFieldsLoading}
                  onChange={(event) => {
                    const value = event.target.value === '' ? '' : Number(event.target.value);
                    onChange(index, 'mappedField' as keyof T, value);
                  }}
                >
                  <option value=''>Select field</option>
                  {(rule.mappingOption === 'NATIVE' ? nativeFields : customFields).map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.display_name || field.api_name}
                    </option>
                  ))}
                </select>
                {mappingFieldsLoading && (
                  <Spinner animation='border' size='sm' className='ms-2 text-primary' />
                )}
              </>
            )}
          </div>
          <div style={priceRuleCellStyle}>
            <div className='d-flex align-items-stretch gap-2'>
              <select
                className='form-select flex-md-1'
                value={rule.adjustmentType}
                onChange={(event) => onChange(index, 'adjustmentType' as keyof T, event.target.value)}
              >
                <option value='NO_CHANGE'>No Change</option>
                <option value='INCREASE'>Increase</option>
                <option value='DECREASE'>Decrease</option>
              </select>
              {rule.adjustmentType !== 'NO_CHANGE' && (
                <>
                  <div className='d-flex align-items-center gap-2 flex-md-1'>
                    <span className='text-muted small mb-0'>by</span>
                    <input
                      type='number'
                      className='form-control flex-fill'
                      placeholder='Enter value'
                      value={rule.adjustmentValue}
                      {...nonNegativeNumberInputProps}
                      onChange={(event) =>
                        handleNumericChange(index, 'adjustmentValue' as keyof T, event.target.value)
                      }
                      style={{ minWidth: '90px' }}
                    />
                  </div>
                  <select
                    className='form-select flex-md-1'
                    value={rule.adjustmentMode}
                    onChange={(event) => onChange(index, 'adjustmentMode' as keyof T, event.target.value)}
                    style={{ minWidth: '120px' }}
                  >
                    <option value='FLAT'>Flat Amount</option>
                    <option value='PERCENTAGE'>Percent</option>
                  </select>
                </>
              )}
            </div>
          </div>
          {hasPriceDateRows && (
            <>
              <div style={priceRuleCellStyle}>
                {rule.isPriceDate && (
                  <input
                    className='form-control'
                    type='date'
                    name={`priceRules[${index}].startDate`}
                    value={rule.startDate}
                    onChange={(e) => onChange(index, 'startDate' as keyof T, e.target.value)}
                  />
                )}
              </div>
              <div style={priceRuleCellStyle}>
                {rule.isPriceDate && (
                  <input
                    className='form-control'
                    type='date'
                    name={`priceRules[${index}].endDate`}
                    value={rule.endDate}
                    onChange={(e) => onChange(index, 'endDate' as keyof T, e.target.value)}
                  />
                )}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};
